import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { DayPicker, DateRange } from 'react-day-picker';
import { format, parseISO, subMonths, isValid } from 'date-fns';
import 'react-day-picker/style.css';

export type PeriodType = 'all' | '7d' | '30d' | '365d' | 'this_month' | 'this_year' | 'custom';

interface TimeFilterProps {
  period: PeriodType;
  customStart?: string;
  customEnd?: string;
  onChange: (period: PeriodType, start?: string, end?: string) => void;
  // Opt-in: existing stats backends treat unknown periods as 7d, so 'all' is only offered where the backend supports it
  includeAllTime?: boolean;
  // Opt-in: smaller fonts/spacing to match dense list pages (e.g. Repairs)
  compact?: boolean;
}

const periodLabels: Record<Exclude<PeriodType, 'all'>, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '365d': 'Last 365 days',
  'this_month': 'This month',
  'this_year': 'This year',
  'custom': 'Custom range'
};

const allTimeLabel = 'All time';

const parseDate = (value?: string): Date | undefined => {
  if (!value) return undefined;
  const date = parseISO(value);
  return isValid(date) ? date : undefined;
};

export const TimeFilter: React.FC<TimeFilterProps> = ({
  period,
  customStart,
  customEnd,
  onChange,
  includeAllTime = false,
  compact = false
}) => {
  const [showCustom, setShowCustom] = useState(period === 'custom');
  const [range, setRange] = useState<DateRange | undefined>(() => {
    const from = parseDate(customStart);
    const to = parseDate(customEnd);
    return from || to ? { from, to } : undefined;
  });

  useEffect(() => {
    if (period === 'custom') {
      setShowCustom(true);
      const from = parseDate(customStart);
      const to = parseDate(customEnd);
      setRange(from || to ? { from, to } : undefined);
    }
  }, [period, customStart, customEnd]);

  const handlePeriodChange = (newPeriod: PeriodType) => {
    if (newPeriod === 'custom') {
      setShowCustom(true);
      // Don't trigger onChange yet - wait for dates to be selected
    } else {
      setShowCustom(false);
      onChange(newPeriod);
    }
  };

  const handleRangeSelect = (selected: DateRange | undefined) => {
    setRange(selected);
    if (selected?.from && selected?.to) {
      onChange('custom', format(selected.from, 'yyyy-MM-dd'), format(selected.to, 'yyyy-MM-dd'));
    }
  };

  const formatDateRange = (): string => {
    const now = new Date();
    let start: Date;
    let end: Date = now;

    switch (period) {
      case 'all':
        return allTimeLabel;
      case '7d':
        start = new Date(now);
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start = new Date(now);
        start.setDate(start.getDate() - 30);
        break;
      case '365d':
        start = new Date(now);
        start.setDate(start.getDate() - 365);
        break;
      case 'this_month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'this_year':
        start = new Date(now.getFullYear(), 0, 1);
        break;
      case 'custom':
        if (customStart && customEnd) {
          return `${new Date(customStart).toLocaleDateString()} - ${new Date(customEnd).toLocaleDateString()}`;
        }
        return 'Select dates';
      default:
        start = new Date(now);
        start.setDate(start.getDate() - 7);
    }

    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };

  return (
    <div className={`bg-white rounded-lg shadow ${compact ? 'p-3 mb-3' : 'p-4 mb-6'}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 mr-4">
          <Calendar className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-gray-500`} />
          <span className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-700`}>Time Period:</span>
        </div>

        <div className="flex flex-wrap gap-2">
          {([...(includeAllTime ? ['all' as PeriodType] : []), ...(Object.keys(periodLabels) as PeriodType[])]).map((p) => (
            <button
              key={p}
              onClick={() => handlePeriodChange(p)}
              className={`${compact ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm'} rounded-md font-medium transition-colors ${
                period === p
                  ? 'bg-naviter-blue text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {p === 'all' ? allTimeLabel : periodLabels[p]}
            </button>
          ))}
        </div>
      </div>

      {showCustom && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <DayPicker
            mode="range"
            numberOfMonths={2}
            selected={range}
            onSelect={handleRangeSelect}
            defaultMonth={range?.from || subMonths(new Date(), 1)}
            endMonth={new Date()}
            disabled={{ after: new Date() }}
            weekStartsOn={1}
            className="text-xs"
            style={{
              '--rdp-accent-color': '#22699D',
              '--rdp-day-width': '30px',
              '--rdp-day-height': '30px',
              '--rdp-day_button-width': '28px',
              '--rdp-day_button-height': '28px',
              '--rdp-nav_button-width': '24px',
              '--rdp-nav_button-height': '24px'
            } as React.CSSProperties}
          />
          {(!range?.from || !range?.to) && (
            <p className="mt-2 text-sm text-gray-500">
              {range?.from
                ? `Start: ${range.from.toLocaleDateString()} — now pick the end date`
                : 'Pick the start date, then the end date'}
            </p>
          )}
        </div>
      )}

      <div className={`${compact ? 'mt-2 text-xs' : 'mt-3 text-sm'} text-gray-500`}>
        Showing data from: <span className="font-medium">{formatDateRange()}</span>
      </div>
    </div>
  );
};

export default TimeFilter;
